import type { CrmFiltersModel } from "../../crm-integration/types/custom-filter-values";

import type { ILeadsSearchLeadsFiltersDto } from "./filter-values";
import type { LeadPriorityScore } from "./lead-priority";

export enum ScoringConfigType {
    User = "user",
    Account = "account",
}

export interface IScoringRuleBusinessContextDto {
    readonly translation?: string;
    readonly actionItem?: string;
}

// Search-composition subset of ILeadsSearchFiltersDto (leadsFilters + salesforceCustomFilters).
// Lives in domain/leads, not domain/search: the domain DAG is leads -> crm-integration -> search,
// so a search/ or crm/ home would create a cycle. Becomes Pick<ILeadsSearchFiltersDto, ...> once
// the unified DTO reaches domain/search (ADR-0006 Phase 5).
export type ScoringRuleFilters = {
    readonly leadsFilters?: Partial<ILeadsSearchLeadsFiltersDto>;
    readonly salesforceCustomFilters?: CrmFiltersModel;
};

export interface IScoringRuleDto {
    readonly id?: string;
    readonly signalIds: readonly string[];
    readonly filters?: ScoringRuleFilters;
    readonly businessContext: IScoringRuleBusinessContextDto;
    readonly teamIds?: readonly number[];
}

export type ScoringConfigRule = Partial<Record<LeadPriorityScore, readonly IScoringRuleDto[]>>;

export interface IScoringConfigDto {
    readonly rules: ScoringConfigRule;
    readonly source: ScoringConfigType;
    readonly createdAt: string;
    readonly updatedAt: string;
}

/**
 * One spreadsheet row of the scoring-config export: a single criterion (signal or filter) with its rule's
 * columns repeated, so the sheet stays filterable. The backend writes every field verbatim — all text is
 * already formatted and translated here.
 */
export interface IScoringRuleExportRowDto {
    readonly priority: string;
    readonly businessContext: string;
    readonly actionItem: string;
    readonly teams: string;
    readonly visibility: string;
    readonly criterionType: string;
    readonly criterionName: string;
    readonly criterionValue: string;
}

export interface IScoringConfigExportDto {
    readonly userEmail: string;
    /** Rendered as-is into the report's "Date Downloaded" cell (dd.MM.yy). */
    readonly date: string;
    readonly rules: readonly IScoringRuleExportRowDto[];
}
